/**
 * Resolve voice-extracted region against the city region list.
 * Unknown neighborhoods → receiver_region_id = '__other__' + free text
 * (same as the manual «آخر» option on new-shipment forms).
 */
export function normalizeRegionQuery(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ');
}

export function findRegionInList(regions, { id, name } = {}) {
  const list = Array.isArray(regions) ? regions : [];
  if (id != null && id !== '' && String(id) !== '__other__') {
    const byId = list.find((r) => String(r.id) === String(id));
    if (byId) return byId;
  }
  const q = normalizeRegionQuery(name);
  if (!q) return null;
  return list.find((r) => {
    const ar = normalizeRegionQuery(r.name_ar);
    const en = normalizeRegionQuery(r.name);
    if (!ar && !en) return false;
    return ar === q || en === q
      || (ar && (ar.includes(q) || q.includes(ar)))
      || (en && (en.includes(q) || q.includes(en)));
  }) || null;
}

/**
 * @param {object} fields - voice LLM / STT fields
 * @param {Array} regionList - regions for the selected city
 * @returns {{
 *   receiver_region_id: string,
 *   receiver_custom_region: string,
 *   receiver_region: string,
 *   usedCustomRegion: boolean,
 * }}
 */
export function resolveVoiceRegionFields(fields = {}, regionList = []) {
  const regionName = String(
    fields.receiver_region
    || fields.receiver_custom_region
    || '',
  ).trim();

  const match = findRegionInList(regionList, {
    id: fields.receiver_region_id,
    name: regionName,
  });

  if (match) {
    return {
      receiver_region_id: String(match.id),
      receiver_custom_region: '',
      receiver_region: match.name_ar || match.name || regionName,
      usedCustomRegion: false,
    };
  }

  if (regionName) {
    return {
      receiver_region_id: '__other__',
      receiver_custom_region: regionName,
      receiver_region: regionName,
      usedCustomRegion: true,
    };
  }

  return {
    receiver_region_id: '',
    receiver_custom_region: '',
    receiver_region: '',
    usedCustomRegion: false,
  };
}

export const VOICE_FILL_REVIEW_HINT =
  'يرجى مراجعة البيانات المدخلة بالصوت قبل الحفظ.';

/**
 * Match spoken merchant name / id against company merchant list.
 */
export function resolveVoiceMerchant(merchants = [], fields = {}) {
  const list = (Array.isArray(merchants) ? merchants : []).filter((m) => m && m.is_active !== 0 && m.is_active !== false);
  if (!list.length) {
    return { merchant: null, merchant_id: '', merchant_name: String(fields.merchant_name || '').trim(), matched: false };
  }

  if (fields.merchant_id != null && fields.merchant_id !== '') {
    const byId = list.find((m) => String(m.id) === String(fields.merchant_id));
    if (byId) {
      return {
        merchant: byId,
        merchant_id: byId.id,
        merchant_name: byId.name || '',
        matched: true,
      };
    }
  }

  const spoken = String(fields.merchant_name || '').trim();
  if (!spoken) {
    return { merchant: null, merchant_id: '', merchant_name: '', matched: false };
  }

  const q = normalizeRegionQuery(spoken);
  let best = null;
  let bestScore = 0;

  for (const m of list) {
    const name = normalizeRegionQuery(m.name);
    if (!name) continue;
    let score = 0;
    if (name === q) score = 100;
    else if (name.includes(q) || q.includes(name)) score = 70 + Math.min(20, Math.min(name.length, q.length));
    else {
      // token overlap (e.g. "متجر الباشا" vs "الباشا")
      const qt = q.split(' ').filter((t) => t.length > 1);
      const nt = name.split(' ').filter((t) => t.length > 1);
      const hits = qt.filter((t) => nt.some((n) => n.includes(t) || t.includes(n)));
      if (hits.length) score = 40 + hits.length * 15;
    }
    if (score > bestScore) {
      bestScore = score;
      best = m;
    }
  }

  if (best && bestScore >= 40) {
    return {
      merchant: best,
      merchant_id: best.id,
      merchant_name: best.name || spoken,
      matched: true,
    };
  }

  return {
    merchant: null,
    merchant_id: '',
    merchant_name: spoken,
    matched: false,
  };
}
